import { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SCALE = 2; // template-image rendu à 2x (points PDF -> pixels)

const ENDPOINTS = {
  daj: { fields: '/pdf-calibration/fields', img: '/pdf-calibration/template-image' },
  attestation: { fields: '/pdf-attest-calibration/fields', img: '/pdf-attest-calibration/template-image' },
};

// Éditeur des rapports système (Demande / Attestation) intégré au module
// Gestion des rapports. Repositionne les champs (et boxes/lignes pour
// l'Attestation) définis par les générateurs métier — la logique de génération
// reste inchangée (on ne modifie que x/y/w/h/fs via les endpoints existants).
export default function SystemReportEditor({ reportKey, nom, onClose }) {
  const ep = ENDPOINTS[reportKey] || ENDPOINTS.daj;
  const [offsetY, setOffsetY] = useState(0);
  const [fields, setFields] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [lines, setLines] = useState([]);
  const [sel, setSel] = useState(null); // {type:'field'|'box'|'line', id}
  const [zoom, setZoom] = useState(1);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [imgUrl] = useState(`${API}${ep.img}?t=${Date.now()}`);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [columns, setColumns] = useState([]);
  const [colFilter, setColFilter] = useState('');

  const imgRef = useRef(null);
  const scrollRef = useRef(null);
  const dragRef = useRef(null);

  // Ajuste le zoom pour que toute la page tienne dans la zone visible
  const fitToPane = (w, h) => {
    const el = scrollRef.current;
    if (!el || !w || !h) return;
    const availW = el.clientWidth - 32;
    const availH = el.clientHeight - 32;
    const z = Math.min(availW / w, availH / h, 1);
    setZoom(Math.max(0.3, Math.round(z * 100) / 100));
  };

  useEffect(() => {
    fetch(`${API}/rapports/system-columns?key=${reportKey}`)
      .then(r => r.json()).then(c => setColumns(Array.isArray(c) ? c : []))
      .catch(() => setColumns([]));
  }, [reportKey]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}${ep.fields}`);
        const cfg = await r.json();
        setOffsetY(cfg.offset_y || 0);
        setFields((cfg.fields || []).map(f => ({ ...f })));
        setBoxes((cfg.boxes || []).map(b => ({ ...b })));
        setLines((cfg.lines || []).map(l => ({ ...l })));
      } catch (e) { setMessage({ type: 'error', text: 'Chargement impossible' }); }
    })();
  }, [ep.fields]);

  const pointFromEvent = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    return {
      x: Math.round(((e.clientX - rect.left) / (SCALE * zoom)) * 10) / 10,
      y: Math.round(((e.clientY - rect.top) / (SCALE * zoom)) * 10) / 10,
    };
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const pt = pointFromEvent(e);
    const special = e.dataTransfer.getData('text/special');
    const col = e.dataTransfer.getData('text/column');
    let nf = null;
    if (special === 'logo') nf = { id: `logo_${Date.now()}`, type: 'logo', x: pt.x, y: pt.y, w: 120, h: 60 };
    else if (special === 'date') nf = { id: `date_${Date.now()}`, type: 'date', x: pt.x, y: pt.y, w: 100, fs: 9 };
    else if (special === 'static') nf = { id: `static_${Date.now()}`, type: 'static', text: 'Texte', x: pt.x, y: pt.y, w: 140, fs: 9 };
    else if (col) nf = { id: `data_${col}_${Date.now()}`, type: 'data', column: col, x: pt.x, y: pt.y, w: 120, fs: 9 };
    if (nf) { setFields(prev => [...prev, nf]); setSel({ type: 'field', id: nf.id }); }
  };

  const deleteField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
    setSel(null);
  };

  const startDrag = (e, type, id, mode) => {
    e.stopPropagation(); e.preventDefault();
    setSel({ type, id });
    let orig;
    if (type === 'field') orig = fields.find(f => f.id === id);
    else if (type === 'box') orig = boxes.find(b => b.id === id);
    else orig = lines.find(l => l.id === id);
    dragRef.current = { type, id, mode, mouseX: e.clientX, mouseY: e.clientY, orig: { ...orig } };
  };

  const onMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.mouseX) / (SCALE * zoom);
    const dy = (e.clientY - d.mouseY) / (SCALE * zoom);
    const rnd = (v) => Math.round(v * 10) / 10;
    if (d.type === 'field') {
      setFields(prev => prev.map(f => {
        if (f.id !== d.id) return f;
        if (d.mode === 'resizewh') return { ...f, w: Math.max(10, rnd((d.orig.w || 120) + dx)), h: Math.max(10, rnd((d.orig.h || 60) + dy)) };
        if (d.mode === 'resize') return { ...f, w: Math.max(10, rnd((d.orig.w || 60) + dx)) };
        return { ...f, x: rnd(d.orig.x + dx), y: rnd(d.orig.y + dy) };
      }));
    } else if (d.type === 'box') {
      setBoxes(prev => prev.map(b => {
        if (b.id !== d.id) return b;
        if (d.mode === 'resize') return { ...b, w: Math.max(10, rnd((d.orig.w || 40) + dx)), h: Math.max(10, rnd((d.orig.h || 40) + dy)) };
        return { ...b, x: rnd(d.orig.x + dx), y: rnd(d.orig.y + dy) };
      }));
    } else {
      setLines(prev => prev.map(l => l.id === d.id
        ? { ...l, x1: rnd(d.orig.x1 + dx), y1: rnd(d.orig.y1 + dy), x2: rnd(d.orig.x2 + dx), y2: rnd(d.orig.y2 + dy) }
        : l));
    }
  }, [zoom]);

  useEffect(() => {
    const up = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', up); };
  }, [onMove]);

  const setFieldProp = (id, key, val) => setFields(prev => prev.map(f => f.id === id ? { ...f, [key]: parseFloat(val) || 0 } : f));
  const setBoxProp = (id, key, val) => setBoxes(prev => prev.map(b => b.id === id ? { ...b, [key]: parseFloat(val) || 0 } : b));
  const setLineProp = (id, key, val) => setLines(prev => prev.map(l => l.id === id ? { ...l, [key]: parseFloat(val) || 0 } : l));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { offset_y: offsetY,
        fields: fields.map(f => {
          const t = (f.type || '').toLowerCase();
          if (t) {
            const o = { id: f.id, type: t, x: f.x, y: f.y };
            if (f.w != null) o.w = f.w;
            if (f.h != null) o.h = f.h;
            if (f.fs != null) o.fs = f.fs;
            if (f.text != null) o.text = f.text;
            if (f.column != null) o.column = f.column;
            return o;
          }
          return { id: f.id, x: f.x, y: f.y, ...(f.w != null ? { w: f.w } : {}), ...(f.h != null ? { h: f.h } : {}), ...(f.fs != null ? { fs: f.fs } : {}) };
        }) };
      if (reportKey === 'attestation') {
        payload.boxes = boxes.map(b => ({ id: b.id, x: b.x, y: b.y, w: b.w, h: b.h }));
        payload.lines = lines.map(l => ({ id: l.id, x1: l.x1, y1: l.y1, x2: l.x2, y2: l.y2 }));
      }
      const res = await fetch(`${API}${ep.fields}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('save');
      setMessage({ type: 'success', text: 'Positions enregistrées !' });
      setTimeout(() => setMessage(null), 2500);
    } catch (e) { setMessage({ type: 'error', text: 'Erreur d\'enregistrement' }); }
    setSaving(false);
  };

  const dispW = nat.w ? nat.w * zoom : 'auto';
  const selField = sel?.type === 'field' && fields.find(f => f.id === sel.id);
  const selBox = sel?.type === 'box' && boxes.find(b => b.id === sel.id);
  const selLine = sel?.type === 'line' && lines.find(l => l.id === sel.id);

  return (
    <div className="popup-overlay" data-testid="system-report-editor" style={{ zIndex: 10002 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           style={{ background: '#f0f0f0', width: 'min(1500px, 98vw)', height: 'min(900px, 96vh)',
                    display: 'flex', flexDirection: 'column', border: '1px solid #555', boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}>
        <div style={{ background: '#1e3a8a', color: '#fff', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 700 }} data-testid="sys-editor-title">Éditeur — {nom} (rapport système)</span>
          <button data-testid="sys-editor-close" onClick={onClose}
                  style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ background: '#dbe6f0', padding: '8px 14px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#334155' }}>
            Déplacez les éléments par glisser ; ajustez finement via les propriétés. La logique métier de génération est conservée.
          </span>
          <label style={{ fontSize: 13 }}>Décalage Y :
            <input type="number" value={offsetY} onChange={e => setOffsetY(parseFloat(e.target.value) || 0)}
                   style={{ width: 70, marginLeft: 6, padding: '3px 6px' }} />
          </label>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="win-btn" onClick={() => setZoom(z => Math.max(0.3, z - 0.1))} style={{ padding: '3px 8px' }}>−</button>
            <span style={{ fontSize: 12 }}>{Math.round(zoom * 100)}%</span>
            <button className="win-btn" onClick={() => setZoom(z => Math.min(2, z + 0.1))} style={{ padding: '3px 8px' }}>+</button>
            <button className="win-btn" onClick={() => fitToPane(nat.w, nat.h)}
                    title="Ajuster à la fenêtre" style={{ padding: '3px 8px' }}>⤢ Ajuster</button>
            <button className="win-btn" data-testid="sys-save-btn" disabled={saving} onClick={save}
                    style={{ padding: '5px 16px', background: '#16a34a', color: '#fff', fontWeight: 700 }}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </span>
          {message && <span data-testid="sys-editor-msg" style={{ fontSize: 13, fontWeight: 700, color: message.type === 'success' ? '#16a34a' : '#dc2626' }}>{message.text}</span>}
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Palette gauche : éléments à ajouter */}
          <div style={{ width: 210, borderRight: '1px solid #aaa', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 8px', fontWeight: 700, fontSize: 13, background: '#e5e7eb' }}>Ajouter un champ</div>
            <div style={{ padding: '6px', borderBottom: '1px solid #ddd', background: '#eef2ff' }}>
              {[['static', '🅰 Texte fixe'], ['date', '📅 Date du jour'], ['logo', '🖼 Logo (région)']].map(([k, lbl]) => (
                <div key={k} draggable data-testid={`sys-special-${k}`}
                     onDragStart={e => e.dataTransfer.setData('text/special', k)}
                     style={{ padding: '5px 8px', fontSize: 12.5, cursor: 'grab', border: '1px dashed #6366f1', borderRadius: 4, marginBottom: 4, background: '#fff' }}>
                  {lbl}
                </div>
              ))}
            </div>
            <div style={{ padding: '4px 8px', fontWeight: 700, fontSize: 12, background: '#eef2ff' }}>Colonnes de données</div>
            <input placeholder="Filtrer…" value={colFilter} onChange={e => setColFilter(e.target.value)}
                   data-testid="sys-col-filter" style={{ margin: 6, padding: '4px 6px', fontSize: 12 }} />
            <div style={{ flex: 1, overflow: 'auto' }}>
              {columns.filter(c => !colFilter || c.name.toLowerCase().includes(colFilter.toLowerCase())).map(c => (
                <div key={c.name} draggable data-testid={`sys-col-${c.name}`}
                     onDragStart={e => e.dataTransfer.setData('text/column', c.name)} title={c.type}
                     style={{ padding: '4px 10px', fontSize: 12, cursor: 'grab', borderBottom: '1px solid #eee' }}
                     onMouseEnter={e => e.currentTarget.style.background = '#e0e7ff'}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {c.name}
                </div>
              ))}
            </div>
          </div>

          {/* Zone PDF */}
          <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', background: '#6b7280', padding: 16 }}>
            <div style={{ position: 'relative', display: 'inline-block' }} data-testid="sys-pdf-canvas"
                 onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
              <img ref={imgRef} src={imgUrl} alt="template" draggable={false}
                   onLoad={e => { setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight }); fitToPane(e.target.naturalWidth, e.target.naturalHeight); }}
                   style={{ display: 'block', maxWidth: 'none', width: dispW }} />

              {/* Lignes (SVG) */}
              {nat.w > 0 && (
                <svg width={nat.w * zoom} height={nat.h * zoom}
                     style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                  {lines.map(l => {
                    const sx = l.x1 * SCALE * zoom, sy = (l.y1 + offsetY) * SCALE * zoom;
                    const ex = l.x2 * SCALE * zoom, ey = (l.y2 + offsetY) * SCALE * zoom;
                    const on = sel?.type === 'line' && sel.id === l.id;
                    return (
                      <g key={l.id} style={{ pointerEvents: 'auto', cursor: 'move' }}
                         onMouseDown={e => startDrag(e, 'line', l.id, 'move')}>
                        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={on ? '#ef4444' : '#0ea5e9'} strokeWidth={on ? 3 : 2} />
                        <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="transparent" strokeWidth={12} />
                      </g>
                    );
                  })}
                </svg>
              )}

              {/* Boxes */}
              {boxes.map(b => {
                const on = sel?.type === 'box' && sel.id === b.id;
                return (
                  <div key={b.id} data-testid={`sys-box-${b.id}`}
                       onMouseDown={e => startDrag(e, 'box', b.id, 'move')}
                       style={{ position: 'absolute', left: b.x * SCALE * zoom, top: (b.y + offsetY) * SCALE * zoom,
                                width: b.w * SCALE * zoom, height: b.h * SCALE * zoom,
                                border: `2px solid ${on ? '#ef4444' : '#f59e0b'}`,
                                background: on ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.08)',
                                cursor: 'move', boxSizing: 'border-box' }}>
                    <div onMouseDown={e => startDrag(e, 'box', b.id, 'resize')}
                         style={{ position: 'absolute', right: -5, bottom: -5, width: 12, height: 12, background: '#f59e0b', border: '1px solid #fff', cursor: 'nwse-resize' }} />
                  </div>
                );
              })}

              {/* Fields */}
              {fields.map(f => {
                const on = sel?.type === 'field' && sel.id === f.id;
                const isImg = f.kind === 'image';
                const utype = (f.type || '').toLowerCase();
                const isUserLogo = utype === 'logo';
                if (isUserLogo) {
                  return (
                    <div key={f.id} data-testid={`sys-field-${f.id}`}
                         onMouseDown={e => startDrag(e, 'field', f.id, 'move')}
                         style={{ position: 'absolute', left: f.x * SCALE * zoom, top: (f.y + offsetY) * SCALE * zoom,
                                  width: (f.w || 120) * SCALE * zoom, height: (f.h || 60) * SCALE * zoom,
                                  border: `2px solid ${on ? '#ef4444' : '#16a34a'}`,
                                  background: on ? 'rgba(239,68,68,0.18)' : 'rgba(22,163,74,0.15)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  color: '#14532d', fontSize: 11, fontWeight: 700, cursor: 'move', boxSizing: 'border-box' }}>
                      🖼 LOGO
                      <div onMouseDown={e => startDrag(e, 'field', f.id, 'resizewh')}
                           style={{ position: 'absolute', right: -5, bottom: -5, width: 12, height: 12, background: '#16a34a', border: '1px solid #fff', cursor: 'nwse-resize' }} />
                    </div>
                  );
                }
                const disp = utype === 'date' ? 'Date du jour'
                  : utype === 'static' ? (f.text || 'Texte')
                  : utype === 'data' ? f.column
                  : isImg ? '🖼' : (f.desc || f.label || f.id);
                const borderCol = on ? '#ef4444' : (isImg ? '#8b5cf6' : (utype ? '#f97316' : '#3b82f6'));
                const bgCol = on ? 'rgba(239,68,68,0.18)' : (isImg ? 'rgba(139,92,246,0.14)' : (utype ? 'rgba(249,115,22,0.16)' : 'rgba(59,130,246,0.12)'));
                const fontPx = Math.max(7, (f.fs || 8) * SCALE * zoom);
                const ascentPx = fontPx * 0.8;
                const baseTop = (f.y + offsetY) * SCALE * zoom; // y = baseline PDF
                if (isImg) {
                  return (
                    <div key={f.id} data-testid={`sys-field-${f.id}`}
                         onMouseDown={e => startDrag(e, 'field', f.id, 'move')}
                         title={f.desc || f.label || f.column || f.id}
                         style={{ position: 'absolute', left: f.x * SCALE * zoom,
                                  top: baseTop, width: (f.w || 60) * SCALE * zoom,
                                  height: (f.h || 30) * SCALE * zoom,
                                  border: `1.5px solid ${borderCol}`, background: bgCol,
                                  color: '#111', fontSize: 12,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'move', boxSizing: 'border-box' }}>
                      {disp}
                      <div onMouseDown={e => startDrag(e, 'field', f.id, 'resize')}
                           style={{ position: 'absolute', right: -4, bottom: -4, width: 9, height: 9, background: borderCol, border: '1px solid #fff', cursor: 'nwse-resize' }} />
                    </div>
                  );
                }
                return (
                  <div key={f.id} data-testid={`sys-field-${f.id}`}
                       onMouseDown={e => startDrag(e, 'field', f.id, 'move')}
                       title="La ligne pointillée = ligne de base du texte à l'impression. Alignez-la sur le trait du document."
                       style={{ position: 'absolute', left: f.x * SCALE * zoom,
                                top: baseTop - ascentPx, height: fontPx,
                                whiteSpace: 'nowrap', cursor: 'move', userSelect: 'none' }}>
                    <span style={{ display: 'inline-block',
                                   fontSize: fontPx, lineHeight: `${fontPx}px`,
                                   fontFamily: 'Helvetica, Arial, sans-serif', color: '#111',
                                   background: bgCol, outline: `1.5px solid ${borderCol}` }}>{disp}</span>
                    <div style={{ position: 'absolute', left: 0, top: ascentPx,
                                  width: 'calc(100% + 20px)',
                                  borderTop: `1px dashed ${borderCol}`, pointerEvents: 'none' }} />
                    <div onMouseDown={e => startDrag(e, 'field', f.id, 'resize')}
                         style={{ position: 'absolute', right: -6, top: 0, width: 9, height: 9, background: borderCol, border: '1px solid #fff', cursor: 'ew-resize' }} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Panneau droit : propriétés + listes */}
          <div style={{ width: 280, borderLeft: '1px solid #aaa', background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '6px 8px', fontWeight: 700, fontSize: 13, background: '#e5e7eb' }}>
              Propriétés
            </div>
            <div style={{ padding: 10, borderBottom: '1px solid #ddd', fontSize: 12, minHeight: 90 }} data-testid="sys-props">
              {selField && (<>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {selField.type === 'logo' ? '🖼 Logo (région)'
                    : selField.type === 'date' ? '📅 Date du jour'
                    : selField.type === 'static' ? '🅰 Texte fixe'
                    : selField.type === 'data' ? `Colonne : ${selField.column}`
                    : (selField.desc || selField.label || selField.id)}
                </div>
                {selField.type === 'static' && (
                  <div style={{ marginBottom: 6 }}>
                    <input data-testid="sys-static-text" value={selField.text || ''}
                           onChange={e => setFields(prev => prev.map(f => f.id === selField.id ? { ...f, text: e.target.value } : f))}
                           placeholder="Texte à imprimer" style={{ width: '100%', padding: '3px 5px', boxSizing: 'border-box' }} />
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 4, alignItems: 'center' }}>
                  <span>X</span><input type="number" value={selField.x} onChange={e => setFieldProp(selField.id, 'x', e.target.value)} />
                  <span>Y</span><input type="number" value={selField.y} onChange={e => setFieldProp(selField.id, 'y', e.target.value)} />
                  <span>Larg.</span><input type="number" value={selField.w ?? 0} onChange={e => setFieldProp(selField.id, 'w', e.target.value)} />
                  {(selField.kind === 'image' || selField.type === 'logo')
                    ? <><span>Haut.</span><input type="number" value={selField.h ?? 0} onChange={e => setFieldProp(selField.id, 'h', e.target.value)} /></>
                    : <><span title="Taille du texte (points), à ajuster selon le texte du PDF">Taille</span><input type="number" data-testid="sys-field-fs" value={selField.fs ?? 0} onChange={e => setFieldProp(selField.id, 'fs', e.target.value)} /></>}
                </div>
                <button className="win-btn" data-testid="sys-field-delete" onClick={() => deleteField(selField.id)}
                        style={{ marginTop: 8, padding: '4px 10px', background: '#dc2626', color: '#fff' }}>Supprimer ce champ</button>
              </>)}
              {selBox && (<>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{selBox.label || selBox.id}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 4, alignItems: 'center' }}>
                  <span>X</span><input type="number" value={selBox.x} onChange={e => setBoxProp(selBox.id, 'x', e.target.value)} />
                  <span>Y</span><input type="number" value={selBox.y} onChange={e => setBoxProp(selBox.id, 'y', e.target.value)} />
                  <span>L</span><input type="number" value={selBox.w} onChange={e => setBoxProp(selBox.id, 'w', e.target.value)} />
                  <span>H</span><input type="number" value={selBox.h} onChange={e => setBoxProp(selBox.id, 'h', e.target.value)} />
                </div>
              </>)}
              {selLine && (<>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>{selLine.label || selLine.id}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '34px 1fr', gap: 4, alignItems: 'center' }}>
                  <span>X1</span><input type="number" value={selLine.x1} onChange={e => setLineProp(selLine.id, 'x1', e.target.value)} />
                  <span>Y1</span><input type="number" value={selLine.y1} onChange={e => setLineProp(selLine.id, 'y1', e.target.value)} />
                  <span>X2</span><input type="number" value={selLine.x2} onChange={e => setLineProp(selLine.id, 'x2', e.target.value)} />
                  <span>Y2</span><input type="number" value={selLine.y2} onChange={e => setLineProp(selLine.id, 'y2', e.target.value)} />
                </div>
              </>)}
              {!sel && <span style={{ color: '#888' }}>Sélectionnez un élément sur le PDF.</span>}
            </div>
            <div style={{ padding: '6px 8px', fontWeight: 700, fontSize: 12, background: '#eef2ff' }}>
              Éléments — {fields.length} champs{reportKey === 'attestation' ? `, ${boxes.length} cadres, ${lines.length} lignes` : ''}
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {fields.map(f => (
                <div key={f.id} onClick={() => setSel({ type: 'field', id: f.id })}
                     style={{ padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', borderBottom: '1px solid #eee',
                              background: sel?.type === 'field' && sel.id === f.id ? '#dbeafe' : 'transparent',
                              display: 'flex', justifyContent: 'space-between' }}>
                  <span>{f.desc || f.label || f.id}</span><span style={{ color: '#888' }}>{f.x},{f.y}</span>
                </div>
              ))}
              {reportKey === 'attestation' && boxes.map(b => (
                <div key={b.id} onClick={() => setSel({ type: 'box', id: b.id })}
                     style={{ padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', borderBottom: '1px solid #eee',
                              background: sel?.type === 'box' && sel.id === b.id ? '#fef3c7' : 'transparent',
                              display: 'flex', justifyContent: 'space-between' }}>
                  <span>▭ {b.label || b.id}</span><span style={{ color: '#888' }}>{b.x},{b.y}</span>
                </div>
              ))}
              {reportKey === 'attestation' && lines.map(l => (
                <div key={l.id} onClick={() => setSel({ type: 'line', id: l.id })}
                     style={{ padding: '4px 10px', fontSize: 11.5, cursor: 'pointer', borderBottom: '1px solid #eee',
                              background: sel?.type === 'line' && sel.id === l.id ? '#e0f2fe' : 'transparent',
                              display: 'flex', justifyContent: 'space-between' }}>
                  <span>／ {l.label || l.id}</span><span style={{ color: '#888' }}>{l.y1}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
